import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone, OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {Equipment} from "../../models/equipment";
import {Assistance, AssistanceType} from "../../models/assistance";
import {
  EditorArrowTool,
  EditorMeasureTool,
  EditorShapeTool,
  EditorTextSizeScalingFactor,
  EditorTool
} from "../../models/editor-tools";
import {EditorToolsStatus} from "../videocall/editor-tools/editor-tools.component";
import {VideocallUnityComponent} from "../videocall/videocall-providers/videocall-unity/videocall-unity.component";
import {
  VideocallBabylonComponent
} from "../videocall/videocall-providers/videocall-babylon/videocall-babylon.component";
import {AccountManagerService} from "../../services/account-manager-service";
import {AuthoringManagerService} from "../../services/authoring-manager.service";
import {MaintenanceManagerService} from "../../services/maintenance-manager.service";
import {RepositoryService} from "../../services/repository.service";
import {SettingsManagerService} from "../../services/settings-manager.service";
import {ScriptService} from "../../services/scripts/script-service";
import {DEBUG, DebugService} from "../../services/debug.service";
import {UiManagerService} from "../../services/ui-manager.service";
import {MatDialog} from "@angular/material/dialog";
import {TranslatePipe} from "@ngx-translate/core";
import {Utils} from "../../utils";
import {DeviceType, VideocallProvider, VideocallStatus} from "../../models/videocall/videocall-status";
import {IVideocallConfig} from "../../models/videocall/i-videocall-confi";
import {environment} from "@environments/environment";
import {Resource, ResourceType, ResourceUtils} from "../../models/resource";
import {
  ChooseResourceDialogComponent,
  ChooseResourceDialogData
} from "../dialogs/choose-resource-dialog/choose-resource-dialog.component";
import {VideocallModule, ExternalCommunicator, ExternalCommunicatorEventType, AnnotatorTool, AnnotatorToolArrowDirection, AnnotatorToolShape} from "videocall-livekit-web";
import {ResourceManager} from "../../services/resource-manager";

enum FileType {
  IMAGE, PDF, MODEL
}

enum RecordingStatus {
  READY,
  STARTING,
  RECORDING,
  STOPPING,
}

@Component({
  selector: 'app-videocall-v2',
  templateUrl: './videocall-v2.component.html',
  styleUrls: ['./videocall-v2.component.scss']
})
export class VideocallV2Component implements OnInit, OnDestroy {

  @Input()
  companyId: string;

  @Input()
  equipment: Equipment;

  @Input()
  assistance: Assistance;

  @Input()
  assistanceType: AssistanceType;

  @Input()
  canEditScene: boolean;

  @Input()
  canRecord: boolean;

  @Input()
  mode: 'ar' | 'noar';

  @Output()
  onModuleLoaded = new EventEmitter();

  @Output()
  onConnected = new EventEmitter();

  @Output()
  onReconnecting = new EventEmitter();

  @Output()
  onReconnected = new EventEmitter();

  @Output()
  onDisconnected = new EventEmitter();

  @Output()
  onRemotePeerConnected = new EventEmitter();

  @Output()
  onRemotePeerDisconnected = new EventEmitter();

  @Output()
  onFullscreenChanged = new EventEmitter<boolean>();

  peerConnected: boolean;

  /* Videocall provider components */
  @ViewChild('videocallModule', { static: false })
  set videocallModuleSetter(value: ElementRef<VideocallModule>) {
    if (value && value.nativeElement) {

      // Get videocall module element
      this.videocallModule = value.nativeElement;

      // Bind events
      this.bindVideocallModuleEvents();

      // Register render loop
      this.registerRenderLoop();

    }
  }
  videocallModule: VideocallModule;
  get externalCommunicator(): ExternalCommunicator {
    return this.videocallModule?.getExternalCommunicator() || null;
  }

  /* Controllers */
  isFreezing: boolean;
  freezed: boolean;
  isMuted: boolean;
  isFullscreen: boolean;

  /* Editor */
  editorTool: EditorTool;
  editorShown: boolean;
  currEditorStatus: EditorToolsStatus;

  /* Videocall status */
  recordingStatus: RecordingStatus = RecordingStatus.READY;
  hasRecorded: boolean;

  constructor(
    private accountManagerService: AccountManagerService,
    private authoringManagerService: AuthoringManagerService,
    private maintenanceManager: MaintenanceManagerService,
    private repositoryService: RepositoryService,
    private settingsManagerService: SettingsManagerService,
    private scriptService: ScriptService,
    private debugService: DebugService,
    private uiManagerService: UiManagerService,
    private resourceManager: ResourceManager,
    private dialog: MatDialog,
    private translatePipe: TranslatePipe,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
  ) { }

  ngOnInit(): void { }

  initVideocallModule() {

    // Initialize module
    this.videocallModule.init();

  }

  private bindVideocallModuleEvents() {
    this.videocallModule.addEventListener("videocall-callback", (event: CustomEvent<{ message: ExternalCommunicatorEventType, data: any }>) => {
      this.onMessageFromModule(event.detail.message, event.detail.data);
    });
  }

  private registerRenderLoop() {
    this.ngZone.runOutsideAngular(() => {
      const loop = () => {
        this.externalCommunicator.render();
        requestAnimationFrame(loop);
      };
      requestAnimationFrame(loop);
    });
  }

  ngOnDestroy() {
    this.externalCommunicator.disconnect();
  }

  public connect(userId: string, userEmail: string): void {
    this.externalCommunicator.connect({
      userId,
      userEmail,
      params: { video: false, audio: true }
    });
  }

  public disconnect(): void {
    this.externalCommunicator.disconnect();
  }

  goFullscreen() {
    this.isFullscreen = !this.isFullscreen;
    if (this.isFullscreen) {
      this.debugService.log(DEBUG.LIVE_ASSISTANCE, 'Going fullscreen...');
      this.uiManagerService.hideSidebar();
    } else {
      this.debugService.log(DEBUG.LIVE_ASSISTANCE, 'Exiting fullscreen...');
      this.uiManagerService.showSidebar();
    }
    this.cdr.detectChanges();
    this.onFullscreenChanged.emit(this.isFullscreen);
    this.externalCommunicator.adjustFrameSize();
  }

  freeze() {
    this.isFreezing = true;
    this.externalCommunicator.freeze();
  }

  unfreeze() {
    this.freezed = false;
    this.isFreezing = false;
    this.editorTool = null;
    this.editorShown = false;
    this.externalCommunicator.applyAnnotations();
  }

  arrow() {
    this.freeze();
    this.showEditor(EditorTool.ARROW);
  }

  shape() {
    this.freeze();
    this.showEditor(EditorTool.SHAPE);
  }

  measure() {
    this.freeze();
    this.showEditor(EditorTool.MEASURE);
  }

  freedraw() {
    this.freeze();
    this.showEditor(EditorTool.FREEHAND);
  }

  selectText() {
    this.freeze();
    this.showEditor(EditorTool.TEXT);
  }

  /*completeTextInput() {
    this.uiManagerService.showTextInputDialog('Input box', 'Value', (value) => {
      this.externalCommunicator.enrichText(`${value.replace(/[,&]/g, ' ')},${(this.currEditorStatus.selectedSubtool + 1) * EditorTextSizeScalingFactor},${Utils.hexToRgbString(this.currEditorStatus.selectedColor)}`);
    }, () => {
      this.externalCommunicator.cancelText();
    });
  }*/

  showEditor(tool: EditorTool) {
    this.editorTool = tool;
    this.editorShown = true;
  }

  onEditorUpdate(status: EditorToolsStatus) {

    switch (status.selectedTool) {

      // Arrow tool was selected
      case EditorTool.ARROW: {

        let direction;
        switch (status.selectedSubtool as EditorArrowTool) {
          case EditorArrowTool.ARROW_LEFT:
            direction = AnnotatorToolArrowDirection.LEFT;
            break;
          case EditorArrowTool.ARROW_UP:
            direction = AnnotatorToolArrowDirection.UP;
            break;
          case EditorArrowTool.ARROW_RIGHT:
            direction = AnnotatorToolArrowDirection.RIGHT;
            break;
          case EditorArrowTool.ARROW_BOTTOM:
            direction = AnnotatorToolArrowDirection.DOWN;
            break;
          case EditorArrowTool.ARROW_CLOCKWISE:
            direction = AnnotatorToolArrowDirection.CLOCKWISE;
            break;
          case EditorArrowTool.ARROW_ANTICLOCKWISE:
            direction = AnnotatorToolArrowDirection.COUNTER_CLOCKWISE;
            break;
        }

        this.externalCommunicator.annotate({
          tool: AnnotatorTool.ARROW,
          props: {
            color: status.selectedColor,
            direction
          }
        });
        break;
      }

      // Shape tool was selected
      case EditorTool.SHAPE: {

        // Hide measurements in module
        // this.currVideocallComponent.hideMeasurements();

        let shape: AnnotatorToolShape;
        switch (status.selectedSubtool as EditorShapeTool) {
          case EditorShapeTool.SHAPE_CIRCLE:
            shape = AnnotatorToolShape.CIRCLE;
            break;
          case EditorShapeTool.SHAPE_RECTANGLE:
            shape = AnnotatorToolShape.RECTANGLE;
            break;
          case EditorShapeTool.SHAPE_LINE:
            shape = AnnotatorToolShape.LINE;
            break;
        }

        this.externalCommunicator.annotate({
          tool: AnnotatorTool.SHAPE,
          props: {
            color: status.selectedColor,
            shape,
            width: (status.selectedWidth + 1) * 3
          }
        });
        break;
      }

      // Measure tool was selected
      case EditorTool.MEASURE: {

        throw new Error('Not implemented.');

        // Show measurements in module
        // this.currVideocallComponent.showMeasurements();

        let shape = '';
        switch (status.selectedSubtool as EditorMeasureTool) {
          case EditorMeasureTool.MEASURE_CIRCLE:
            shape = 'circle';
            break;
          case EditorMeasureTool.MEASURE_RECTANGLE:
            shape = 'rectangle';
            break;
          case EditorMeasureTool.MEASURE_LINE:
            shape = 'line';
            break;
        }

        // this.currVideocallComponent.selectShape(`${Utils.hexToRgbString(status.selectedColor)},${shape},${status.selectedWidth + 1}`);
        break;
      }

      // Freedraw tool was selected
      case EditorTool.FREEHAND: {
        this.externalCommunicator.annotate({
          tool: AnnotatorTool.FREEHAND,
          props: {
            color: status.selectedColor, // Utils.hexToRgbString(status.selectedColor),
            width: (status.selectedWidth + 1) * 3
          }
        });
        break;
      }

      // Text tool was selected
      case EditorTool.TEXT: {

        throw new Error('Not implemented.');

        // Save editor status for text input properties
        this.currEditorStatus = status;

        // this.externalCommunicator.selectText();
        break;
      }

      default:
        this.debugService.error(DEBUG.VIDEOCALL, `Unknown editor subtool: ${status.selectedSubtool}`);
        break;
    }

  }

  clearDrawings() {
    this.externalCommunicator.clearAnnotations();
  }

  mute() {
    this.isMuted = !this.isMuted;
    if (this.isMuted) {
      this.externalCommunicator.mute();
    } else {
      this.externalCommunicator.unmute();
    }
    this.updateUI();
  }

  addImage() {
    this.selectFile(FileType.IMAGE, async (file: File) => {
      const url = await this.authoringManagerService.uploadVideocallContent(this.assistance, ResourceType.IMAGE, file);
      this.sendImageToModule(url);
    });
  }

  addModel() {
    this.selectFile(FileType.MODEL, async (file: File) => {
      const url = await this.authoringManagerService.uploadVideocallContent(this.assistance, ResourceType.MODEL, file);
      this.sendModelToModule(url);
    });
  }

  addPdf() {
    this.selectFile(FileType.PDF, async (file: File) => {
      const url = await this.authoringManagerService.uploadVideocallContent(this.assistance, ResourceType.DOC, file);
      this.sendPDFToModule(url);
    });
  }

  async addFromLibrary() {

    const dialogRef = this.dialog.open(ChooseResourceDialogComponent, {
      width: '1200px',
      data: {equipment: this.equipment, rows: 2, types: [ResourceType.DOC]} as ChooseResourceDialogData,
    });
    const result: ChooseResourceDialogData = await dialogRef.afterClosed().toPromise();

    if (result) {

      // Perform bind between resource and maintenance
      this.authoringManagerService.bindResourceToMaintenance(this.assistance, result.resource);

      switch (result.resource.type) {
        case ResourceType.IMAGE:
          this.sendImageToModule(result.resource.url);
          break;
        case ResourceType.MODEL:
          this.sendModelToModule(result.resource.url);
          break;
        case ResourceType.DOC:
          this.sendPDFToModule(result.resource.url);
          break;
      }
    }

    this.updateUI();

  }

  sendImageToModule(url: string) {
    this.externalCommunicator.showImage({ url });
    this.uiManagerService.notify('Image uploaded successfully');
  }

  sendModelToModule(url: string) {
    this.externalCommunicator.showModel({ url });
    this.uiManagerService.notify('Model uploaded successfully');
  }

  sendPDFToModule(url: string) {
    this.externalCommunicator.showPDF({ url });
    this.uiManagerService.notify('PDF uploaded successfully');
  }

  selectFile(fileType: FileType, callback: (file: File) => void) {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';

    switch (fileType) {
      case FileType.IMAGE:
        fileInput.accept = ResourceUtils.ResourceTypesToContentType([ResourceType.IMAGE]);
        break;
      case FileType.MODEL:
        fileInput.accept = ResourceUtils.ResourceTypesToContentType([ResourceType.MODEL]).replace('model/gltf-binary', '.gltf,.glb'); // Fix required to get right files
        break;
      case FileType.PDF:
        fileInput.accept = ResourceUtils.ResourceTypesToContentType([ResourceType.DOC]);
        break;
    }

    fileInput.onchange = e => {
      callback(e.target['files'][0]);
    };
    fileInput.click();
  }

  async startRecording() {
    if (this.hasRecorded) {
      this.uiManagerService.showConfirmDialog(
        'Record maintenance',
        'This maintenance has already been recorder. Do you want to overwrite the recording?',
        () => {
          this.doStartRecording();
          this.updateUI();
        }
      );
    } else {
      await this.doStartRecording();
    }
  }

  async doStartRecording() {

    // Exit if not in ready state
    if (this.recordingStatus !== RecordingStatus.READY) {
      this.debugService.log(DEBUG.LIVE_ASSISTANCE, 'Not in ready state to start recording...');
      return;
    }

    // Log
    this.debugService.log(DEBUG.LIVE_ASSISTANCE, `Record is starting with room ${this.assistance.id}...`);

    // Start recording
    await this.externalCommunicator.startRecording();

    // Set recording status
    this.recordingStatus = RecordingStatus.RECORDING;

  }

  stopRecording(showDialog: boolean) {

    // Exit if not recording anything
    if (this.recordingStatus !== RecordingStatus.RECORDING) {
      this.debugService.log(DEBUG.LIVE_ASSISTANCE, 'Not in recording state to stop recording...');
      return;
    }

    // Stop recording action
    const stopRecording = async () => {

      // Log
      this.debugService.log(DEBUG.LIVE_ASSISTANCE, `Record is stopping with room ${this.assistance.id}...`);

      // Stop recording
      await this.externalCommunicator.stopRecording();

      // Finalize recording
      await this.finalizeRecording();

      // Set recording status
      this.recordingStatus = RecordingStatus.READY;

    };

    if (showDialog) {
      this.uiManagerService.showConfirmDialog(
        'Stop recording',
        'Do you want to stop the recording',
        () => {
          stopRecording();
          this.updateUI();
        }
      );
    } else {
      stopRecording();
    }

  }

  private async finalizeRecording() {

    // Fetch tags from maintenance
    // TODO

    // Create resource
    let recordingResource = new Resource();
    recordingResource.name = `Recording ${new Date().toLocaleDateString()} - ${new Date().toLocaleTimeString()}`;
    recordingResource.modelId = this.assistance.equipment.model.id;
    recordingResource.typeId = ResourceType.VIDEO;
    recordingResource.tags = [];
    recordingResource = await this.resourceManager.createResource(recordingResource, false);

    // Set content type
    const contentType = 'video/mp4';

    // Set file to resource
    await this.resourceManager.setFile(
      recordingResource.id,
      environment.videocall.bucket,
      `livekit-recorder/room-scotty-${this.assistance.id}.mp4`,
      contentType
    );

    // Force content type
    recordingResource.contentType = contentType;

    // Bind recording to maintenance
    if (this.assistance.maintenance) {
      await this.maintenanceManager.setMaintenanceRecording(
        this.assistance.maintenance,
        environment.videocall.bucket,
        `livekit-recorder/room-scotty-${this.assistance.id}.mp4`,
        contentType
      );
    }

  }

  onMessageFromModule(type: ExternalCommunicatorEventType, data?: string): void {

    switch (type) {
      case ExternalCommunicatorEventType.MODULE_LOADED:
        this.onModuleLoadedEvent();
        break;
      case ExternalCommunicatorEventType.CONNECTED:
        this.onConnectedEvent();
        break;
      case ExternalCommunicatorEventType.RECONNECTING:
        this.onReconnectingEvent();
        break;
      case ExternalCommunicatorEventType.RECONNECTED:
        this.onReconnectedEvent();
        break;
      case ExternalCommunicatorEventType.DISCONNECTED:
        this.onDisconnectedEvent();
        break;
      case ExternalCommunicatorEventType.REMOTE_PEER_CONNECTED:
        this.onRemotePeerConnectedEvent();
        break;
      case ExternalCommunicatorEventType.REMOTE_PEER_DISCONNECTED:
        this.onRemotePeerDisconnectedEvent();
        break;
      case ExternalCommunicatorEventType.FREEZED:
        this.onFreezed();
        break;
      case ExternalCommunicatorEventType.UNFREEZED:
        this.onUnfreezed();
        break;
      case ExternalCommunicatorEventType.ANNOTATIONS_APPLIED:
        this.onAnnotationsApplied();
        break;
      default:
        this.debugService.log(DEBUG.LIVE_ASSISTANCE, `Event not handled: ${type}` + (data ? ` with data: ${data}` : ''));
    }
    this.updateUI();
  }

  private onModuleLoadedEvent(): void {
    this.onModuleLoaded.emit();
  }

  private onConnectedEvent(): void {
    this.onConnected.emit();
  }

  private onReconnectingEvent(): void {
    this.onReconnecting.emit();
  }

  private onReconnectedEvent(): void {
    this.onReconnected.emit();
  }

  private onDisconnectedEvent(): void {
    this.onDisconnected.emit();
  }

  private onRemotePeerConnectedEvent(): void {
    this.peerConnected = true;
    this.onRemotePeerConnected.emit();
  }

  private onRemotePeerDisconnectedEvent(): void {
    this.peerConnected = false;
    this.onRemotePeerDisconnected.emit();
  }

  private onFreezed(): void {
    this.isFreezing = false;
    this.freezed = true;
  }

  public onUnfreezed(): void {
    this.isFreezing = false;
  }

  public onAnnotationsApplied(): void {
    this.isFreezing = false;
  }

  /*onRecordStarted() {
    this.recordingStatus = RecordingStatus.RECORDING;
    this.hasRecorded = true;
    this.debugService.log(DEBUG.LIVE_ASSISTANCE, 'Recording is started');
  }*/

  /*onRecordStopped() {
    this.recordingStatus = RecordingStatus.READY;
    this.debugService.log(DEBUG.LIVE_ASSISTANCE, 'Recording is stopped');
  }*/

  /*onAnnotationText() {
    // A text field was added in scene -> input text
    this.completeTextInput();
  }*/

  /*onCallClosed() {
    this.stopCall();
  }*/

  onModuleUnloaded() {
    /*this.isVideocallStarted = false;
    this.isVideocallCompleted = true;*/
  }

  updateUI() {
    this.cdr.detectChanges();
  }

  get RecordingStatus() { return RecordingStatus; }

  get AssistanceType() { return AssistanceType; }

  get environment() { return environment; }

}
